import {
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BranchesOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  FileExcelOutlined,
  FileSyncOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  FormOutlined,
  HistoryOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  SnippetsOutlined,
  UserOutlined
} from '@ant-design/icons';
import {users} from '@mep/pae-commons';
import React from 'react';

import {USER_ACTION_TYPE} from '@/components/UserOptions/constants';
import {useHandleClick} from '@/components/UserOptions/hooks';

const {USER_TYPES} = users;

const functionariesBase = [USER_TYPES.FUNCTIONARY, USER_TYPES.FUNCTIONARY_ORGANIZATION, USER_TYPES.ONAT_USER];
const userTypes = [USER_TYPES.HISTORIAN, ...functionariesBase];

/**
 * Navigation define all the routes and link that
 * will be render in the sidebar or navbar(depending of the layout)
 *
 * It can be an
 * {Array} Use array when yo have group sections, like Main, Admin , etc
 * Ex: [{title:'name', menu:[...],}, ....]
 *
 * {Object} Use object when yo don't have section , it is just one,
 * Ex: {menu: [.....]}
 * */
export const navigation = [
  {
    title: 'statistics',
    userTypes: functionariesBase,
    permissions: ['REPORT:READ'],
    menu: [
      {
        title: 'generalStatistics',
        exact: 'true',
        link: '/dashboard/general',
        permissions: ['REPORT:READ'],
        icon: <BarChartOutlined/>
      },
      {
        title: 'proposalStatistics',
        exact: 'true',
        link: '/dashboard/proposal-request',
        permissions: ['REPORT:READ', 'REPORT:GENERAL_PROPOSAL'],
        icon: <SnippetsOutlined/>
      },
      {
        title: 'proposalSummaryStatistics',
        exact: 'true',
        link: '/dashboard/proposal-summary-request',
        permissions: ['REPORT:READ', 'REPORT:APPROVED_PROPOSALS'],
        icon: <SnippetsOutlined/>
      },
      {
        title: 'onatFiles',
        exact: 'true',
        link: '/dashboard/expedients-onat',
        permissions: ['REPORT:READ', 'REPORT:EXPEDIENTS_ONAT'],
        icon: <FolderOpenOutlined/>
      },
      {
        title: 'bankFiles',
        exact: 'true',
        link: '/dashboard/expedients-bank',
        permissions: ['REPORT:READ', 'REPORT:EXPEDIENTS_BANK'],
        icon: <FolderOpenOutlined/>
      },
      {
        title: 'pendingBank',
        exact: 'true',
        link: '/dashboard/pending-bank',
        permissions: ['REPORT:READ', 'REPORT:PENDING_BANK_CI'],
        icon: <SnippetsOutlined/>
      }
    ]
  },
  {
    title: 'models',
    permissions: ['REQUEST:READ', 'EXPEDIENT:READ', 'ACTIVITY_PLACE:READ', 'COMPANY:READ', 'TCP:READ'],
    menu: [
      {
        link: '/proposal-request/requests',
        icon: <SnippetsOutlined/>,
        permissions: ['REQUEST:READ'],
        userTypes
      },
      {
        link: '/proposal-files/expedients',
        icon: <FolderOutlined/>,
        permissions: ['EXPEDIENT:READ'],
        userTypes: [USER_TYPES.COMMERCIAL_REGISTER, USER_TYPES.NOTARY, USER_TYPES.NOTARY_DPJ, ...functionariesBase]
      },
      {
        link: '/proposal-files/processed-expedients',
        icon: <FolderOutlined/>,
        permissions: ['EXPEDIENT:READ'],
        noAdminLink: true,
        userTypes: [USER_TYPES.COMMERCIAL_REGISTER, USER_TYPES.NOTARY, USER_TYPES.NOTARY_DPJ]
      },
      {
        title: 'tcps',
        icon: <AuditOutlined/>,
        link: '/company/tcp',
        permissions: ['TCP:READ'],
        userTypes: [USER_TYPES.ONAT_USER]
      },
      {
        title: 'activityplace',
        icon: <EnvironmentOutlined/>,
        link: '/activityplace/activity-places',
        permissions: ['ACTIVITY_PLACE:READ'],
        userTypes: functionariesBase
      },
      {
        title: 'procedures',
        icon: <FormOutlined/>,
        link: '/company/procedures',
        permissions: ['PROCEDURE:READ'],
        userTypes: [USER_TYPES.NOTARY, USER_TYPES.NOTARY_DPJ, USER_TYPES.COMMERCIAL_REGISTER, ...functionariesBase]
      },
      {
        link: '/company/processed-procedures',
        icon: <FormOutlined/>,
        permissions: ['PROCEDURE:READ'],
        noAdminLink: true,
        userTypes: [USER_TYPES.COMMERCIAL_REGISTER, USER_TYPES.NOTARY, USER_TYPES.NOTARY_DPJ]
      },
    ]
  },
  {
    title: 'admin',
    permissions: ['CHECK_LIST:WRITE', 'ACTOR:WRITE', 'PROCESS_SETTINGS:WRITE', 'USER:WRITE', 'TRACE:READ', 'USER:READ', 'OFFICIAL:READ'],
    menu: [
      {
        title: 'verification',
        icon: <BranchesOutlined/>,
        permissions: ['CHECK_LIST:WRITE', 'ACTOR:WRITE', 'PROCESS_SETTINGS:WRITE'],
        menu: [
          {
            link: '/verification/check-lists',
            permissions: ['CHECK_LIST:WRITE']
          },
          {
            link: '/actors/configuration',
            permissions: ['ACTOR:WRITE']
          },
          {
            link: '/actors/process',
            permissions: ['PROCESS_SETTINGS:WRITE']
          }
        ]
      },
      {
        title: 'security',
        path: '/admin', // we use path to append it as prefix to the menu children
        icon: <SafetyCertificateOutlined/>,
        permissions: ['USER:READ', 'TRACE:READ', 'OFFICIAL:READ', 'USER:INVITE'],
        menu: [
          {
            link: '/users',
            permissions: ['USER:READ']
          },
          {
            link: '/officers',
            permissions: ['OFFICIAL:READ']
          },
          {
            link: '/invites',
            permissions: ['USER:INVITE']
          },
          {
            link: '/permissions',
            permissions: ['ADMIN']
          },
          {
            link: '/traces',
            permissions: ['TRACE:READ']
          },
        ]
      }
    ]
  },
  {
    title: 'settings',
    userTypes: functionariesBase,
    permissions: ['ORGANIZATION:READ', 'CNAE_ACTIVITY:READ', 'OCPI_NAME:READ', 'BANK_OFFICE:READ',
      'EXPORT_ENTITY:READ', 'TECH_PARK:READ', 'FAQ:READ', 'PROTECTED_AREA:READ', 'HISTORIAN_OFFICES:READ',
      'NOTARY_OFFICES:READ', 'DPJ:READ', 'COMMERCIAL_REGISTER:READ', 'PERMISSION_ACTIVITY:READ', 'ACTIVITY:READ'],
    menu: [
      {
        title: 'organizations',
        path: '/configuration',
        icon: <ApartmentOutlined/>,
        permissions: ['ORGANIZATION:READ', 'HISTORIAN_OFFICES:READ', 'NOTARY_OFFICES:READ', 'DPJ:READ'],
        menu: [
          {
            link: '/historian-offices',
            permissions: ['HISTORIAN_OFFICES:READ']
          },
          {
            link: '/notary-offices',
            permissions: ['NOTARY_OFFICES:READ']
          },
          {
            link: '/dpj-offices',
            permissions: ['DPJ:READ']
          },
          {
            link: '/commercial-registers',
            permissions: ['COMMERCIAL_REGISTER:READ']
          },
          {
            link: '/organizations',
            permissions: ['ORGANIZATION:READ']
          }
        ]
      },
      {
        title: 'nomenclators',
        path: '/configuration',
        icon: <AppstoreOutlined/>,
        permissions: ['CNAE_ACTIVITY:READ', 'OCPI_NAME:READ', 'BANK_OFFICE:READ', 'EXPORT_ENTITY:READ',
          'TECH_PARK:READ', 'PROTECTED_AREA:READ', 'PERMISSION_ACTIVITY:READ', 'ACTIVITY:READ'],
        menu: [
          {
            link: '/causes',
            permissions: ['CAUSE:READ']
          },
          {
            link: '/cnae-nomenclators',
            permissions: ['CNAE_ACTIVITY:READ']
          },
          {
            link: '/activitys',
            permissions: ['ACTIVITY:READ']
          },
          {
            link: '/permission-activities',
            permissions: ['PERMISSION_ACTIVITY:READ']
          },
          {
            link: '/ocpi-registers',
            permissions: ['OCPI_NAME:READ']
          },
          {
            link: '/bank-offices',
            permissions: ['BANK_OFFICE:READ']
          },
          {
            link: '/export-entities',
            permissions: ['EXPORT_ENTITY:READ']
          },
          {
            link: '/tech-parks',
            permissions: ['TECH_PARK:READ']
          },
          {
            link: '/protected-areas',
            permissions: ['PROTECTED_AREA:READ']
          },
          // {
          //   link: '/categories',
          //   permissions: ['CATEGORY:READ']
          // }
        ]
      },
      {
        title: 'advanced',
        icon: <SettingOutlined/>,
        permissions: ['FAQ:WRITE', 'PAGE:WRITE'],
        menu: [
          {
            link: '/configuration/faqs',
            permissions: ['FAQ:WRITE']
          },
          {
            link: '/cms/admin',
            permissions: ['PAGE:WRITE']
          }
        ]
      }
    ]
  },
  {
    title: 'tools',
    userTypes: functionariesBase,
    permissions: ['SETTING:WRITE', 'APPROVAL_QUEUE:READ', 'REQUEST:READ'],
    menu: [
      {
        link: '/proposal-request/all_requests',
        icon: <SnippetsOutlined/>,
        permissions: ['REQUEST:READ'],
        userTypes
      },
      {
        link: '/configuration/advanced',
        icon: <SettingOutlined/>,
        permissions: ['SETTING:WRITE'],
      },
      {
        title: 'bankSync',
        exact: 'true',
        link: '/dashboard/bank-sync-process',
        permissions: ['ADMIN'],
        icon: <FileSyncOutlined/>
      },
      {
        link: '/tools/approval-queues',
        icon: <SnippetsOutlined/>,
        permissions: ['APPROVAL_QUEUE:READ', 'REQUEST:READ'],
        userTypes
      },
      {
        link: '/tools/export-document-history',
        icon: <FileExcelOutlined/>,
        permissions: ['EXPORT_DOCUMENT_HISTORY:READ'],
        userTypes: functionariesBase
      },
      {
        icon: <HistoryOutlined/>,
        link: '/admin/changelog',
        permissions: ['ADMIN'],
      },
    ]
  }
];

export const userOptions = {
  userActions: {
    viewType: USER_ACTION_TYPE.POPUP,
    showInfo: true,
    options: [
      {
        divider: true
      },
      {
        title: 'profile',
        action: 'PROFILE',
        icon: <UserOutlined/>,
      },
      {
        divider: true
      },
      {
        title: 'logout',
        action: 'LOGOUT',
        icon: <ExportOutlined/>,
      }
    ],
  },
  useHandleClick
};

export const publicContent = [
  // {
  //   link: '/',
  //   title: 'home',
  // },
  // {
  //   link: '/documents',
  //   title: 'formation',
  // },
  // {
  //   link: '/notices',
  //   title: 'notices',
  // },
  {
    link: '/preguntas-frecuentes',
    title: 'faq',
  }
];
